import { create } from 'zustand';
import { Order, Position, Account } from '@shared/types';

interface SignalItem {
  id: string;
  strategyName: string;
  action: 'BUY' | 'SELL';
  type: 'MARKET' | 'LIMIT' | 'STOP' | 'STOP_LIMIT';
  quantity: number;
  price?: number;
  stopLoss?: number;
  takeProfit?: number;
  reason: string;
  timestamp: number;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'EXECUTED';
}

interface OrderState {
  // Signals
  signalQueue: SignalItem[];
  signals: SignalItem[];
  pendingSignalsCount: number;

  // Orders
  orders: Order[];
  activeOrders: Order[];

  // Positions
  positions: Position[];

  // Account
  account: Account | null;
  accountBalance: number;
  dailyPnL: number;

  // Actions - Signals
  addSignal: (signal: SignalItem) => void;
  updateSignal: (id: string, updates: Partial<SignalItem>) => void;
  removeSignal: (id: string) => void;
  clearOldSignals: () => void;
  approveSignal: (id: string) => void;
  rejectSignal: (id: string) => void;

  // Actions - Orders
  addOrder: (order: Order) => void;
  updateOrder: (id: string, updates: Partial<Order>) => void;
  setOrders: (orders: Order[]) => void;

  // Actions - Positions
  setPositions: (positions: Position[]) => void;
  updatePosition: (symbol: string, updates: Partial<Position>) => void;

  // Actions - Account
  setAccount: (account: Account) => void;
  setAccountBalance: (balance: number) => void;
  setDailyPnL: (pnl: number) => void;
  updateDailyPnL: (pnl: number) => void;

  // Reset
  reset: () => void;
}

export const useOrderStore = create<OrderState>((set) => ({
  signalQueue: [],
  signals: [],
  pendingSignalsCount: 0,
  orders: [],
  activeOrders: [],
  positions: [],
  account: null,
  accountBalance: 100000,
  dailyPnL: 0,

  // Signal actions
  addSignal: (signal: SignalItem) =>
    set((state) => {
      const newSignals = [...state.signals, signal];
      const newSignalQueue = [...state.signalQueue, signal];
      const pendingCount = newSignals.filter((s) => s.status === 'PENDING').length;
      return {
        signals: newSignals,
        signalQueue: newSignalQueue,
        pendingSignalsCount: pendingCount,
      };
    }),

  approveSignal: (id: string) =>
    set((state) => {
      const newSignalQueue = state.signalQueue.map((s) =>
        s.id === id ? { ...s, status: 'APPROVED' as const } : s
      );
      const newSignals = state.signals.map((s) =>
        s.id === id ? { ...s, status: 'APPROVED' as const } : s
      );
      return {
        signalQueue: newSignalQueue,
        signals: newSignals,
        pendingSignalsCount: newSignalQueue.filter((s) => s.status === 'PENDING').length,
      };
    }),

  rejectSignal: (id: string) =>
    set((state) => {
      const newSignalQueue = state.signalQueue.map((s) =>
        s.id === id ? { ...s, status: 'REJECTED' as const } : s
      );
      const newSignals = state.signals.map((s) =>
        s.id === id ? { ...s, status: 'REJECTED' as const } : s
      );
      return {
        signalQueue: newSignalQueue,
        signals: newSignals,
        pendingSignalsCount: newSignalQueue.filter((s) => s.status === 'PENDING').length,
      };
    }),

  updateSignal: (id: string, updates: Partial<SignalItem>) =>
    set((state) => {
      const newSignals = state.signals.map((s) =>
        s.id === id ? { ...s, ...updates } : s
      );
      const newSignalQueue = state.signalQueue.map((s) =>
        s.id === id ? { ...s, ...updates } : s
      );
      const pendingCount = newSignals.filter((s) => s.status === 'PENDING').length;
      return {
        signals: newSignals,
        signalQueue: newSignalQueue,
        pendingSignalsCount: pendingCount,
      };
    }),

  removeSignal: (id: string) =>
    set((state) => {
      const newSignals = state.signals.filter((s) => s.id !== id);
      const newSignalQueue = state.signalQueue.filter((s) => s.id !== id);
      const pendingCount = newSignals.filter((s) => s.status === 'PENDING').length;
      return {
        signals: newSignals,
        signalQueue: newSignalQueue,
        pendingSignalsCount: pendingCount,
      };
    }),

  clearOldSignals: () =>
    set((state) => ({
      signals: state.signals.slice(-100),
      signalQueue: state.signalQueue.slice(-100),
    })),

  // Order actions
  addOrder: (order: Order) =>
    set((state) => {
      const newOrders = [...state.orders, order];
      const activeOrders = newOrders.filter(
        (o) => o.status === 'PENDING' || o.status === 'FILLED'
      );
      return {
        orders: newOrders,
        activeOrders,
      };
    }),

  updateOrder: (id: string, updates: Partial<Order>) =>
    set((state) => {
      const newOrders = state.orders.map((o) =>
        o.id === id ? { ...o, ...updates } : o
      );
      const activeOrders = newOrders.filter(
        (o) => o.status === 'PENDING' || o.status === 'FILLED'
      );
      return {
        orders: newOrders,
        activeOrders,
      };
    }),

  setOrders: (orders: Order[]) =>
    set({
      orders,
      activeOrders: orders.filter(
        (o) => o.status === 'PENDING' || o.status === 'FILLED'
      ),
    }),

  // Position actions
  setPositions: (positions: Position[]) =>
    set({ positions }),

  updatePosition: (symbol: string, updates: Partial<Position>) =>
    set((state) => ({
      positions: state.positions.map((p) =>
        p.symbol === symbol ? { ...p, ...updates } : p
      ),
    })),

  // Account actions
  setAccount: (account: Account) =>
    set({ account }),

  setAccountBalance: (balance: number) =>
    set({ accountBalance: balance }),

  setDailyPnL: (pnl: number) =>
    set({ dailyPnL: pnl }),

  updateDailyPnL: (pnl: number) =>
    set((state) => ({ dailyPnL: state.dailyPnL + pnl })),

  // Reset
  reset: () =>
    set({
      signalQueue: [],
      signals: [],
      pendingSignalsCount: 0,
      orders: [],
      activeOrders: [],
      positions: [],
      account: null,
      accountBalance: 100000,
      dailyPnL: 0,
    }),
}));
